import * as React from 'react'
import {
  Camera,
  Clock,
  Download,
  Eye,
  Film,
  Lightbulb,
  Palette,
  Scissors,
  Sparkles,
  Star,
  Video,
  VideoOff,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { formatDate } from '@/lib/utils'
import { apiFetch } from '@/lib/api'
import type { ReferenceVideo } from '@/types'

interface Props {
  reference: ReferenceVideo | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onRefresh: () => void
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </h3>
      <div className="text-sm">{children}</div>
    </div>
  )
}

function VideoPreviewBlock({ src, title }: { src: string; title: string }) {
  const [error, setError] = React.useState(false)

  if (error) {
    return (
      <div className="mb-4 rounded-lg border border-border bg-muted/30 flex flex-col items-center justify-center py-10 gap-3">
        <VideoOff className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Preview unavailable for this video format</p>
        <a
          href={src}
          download={title}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
        >
          <Download className="h-3.5 w-3.5" /> Download original
        </a>
      </div>
    )
  }

  return (
    <div className="mb-4">
      <video
        src={src}
        className="w-full rounded-lg border border-border bg-black"
        controls
        preload="metadata"
        onError={() => setError(true)}
      />
    </div>
  )
}

function ProfileField({ label, value }: { label: string; value: string | undefined | null }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-2 py-0.5">
      <span className="text-xs text-muted-foreground min-w-[100px]">{label}:</span>
      <span className="text-xs capitalize">{value}</span>
    </div>
  )
}

export function ReferenceDetailModal({ reference, open, onOpenChange, onRefresh }: Props) {
  if (!reference) return null

  const p = reference.style_profile || {}
  const comp = (p.shot_composition || {}) as Record<string, any>
  const cam = (p.camera_movement || {}) as Record<string, any>
  const color = (p.color_grading || {}) as Record<string, any>
  const pacing = (p.pacing || {}) as Record<string, any>
  const light = (p.lighting || {}) as Record<string, any>
  const mood = (p.mood || {}) as Record<string, any>
  const hints = (p.recommended_prompt_hints || []) as string[]
  const hasProfile = Object.keys(p).length > 0

  const toggleFavorite = async (styleId: number, current: boolean) => {
    await apiFetch(`/api/v1/references/styles/${styleId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_favorite: !current }),
    })
    onRefresh()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="text-lg">{reference.title}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-80px)] px-6 pb-6">
          {/* Video preview */}
          {reference.local_path && (
            <VideoPreviewBlock src={`/uploads/${reference.local_path}`} title={reference.title} />
          )}

          {reference.source_url && (
            <div className="mb-4 rounded-md border border-border bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground">Source URL</p>
              <a
                href={reference.source_url}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-foreground underline break-all"
              >
                {reference.source_url}
              </a>
            </div>
          )}

          {/* Analysis status */}
          <div className="flex items-center gap-3 mb-4">
            <Badge
              variant={reference.analysis_status === 'completed' ? 'success' : reference.analysis_status === 'failed' ? 'destructive' : 'secondary'}
            >
              {reference.analysis_status}
            </Badge>
            <span className="text-xs text-muted-foreground">{formatDate(reference.created_at)}</span>
            {reference.file_size_bytes && (
              <span className="text-xs text-muted-foreground">
                {(reference.file_size_bytes / (1024 * 1024)).toFixed(1)} MB
              </span>
            )}
          </div>

          {reference.error_message && (
            <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {reference.error_message}
            </div>
          )}

          {/* Summary */}
          {p.summary && (
            <div className="mb-4 rounded-md border border-border bg-muted/30 p-3">
              <p className="text-sm leading-relaxed">{p.summary as string}</p>
            </div>
          )}

          {hasProfile && (
            <>
              <Separator className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Shot Composition */}
                <Section icon={Eye} title="Shot Composition">
                  <ProfileField label="Framing" value={comp.primary_framing} />
                  <ProfileField label="Depth of Field" value={comp.depth_of_field} />
                  <ProfileField label="Symmetry" value={comp.symmetry} />
                  {comp.notes && <p className="text-[11px] text-muted-foreground mt-1">{comp.notes}</p>}
                </Section>

                {/* Camera Movement */}
                <Section icon={Camera} title="Camera Movement">
                  <ProfileField label="Movement" value={cam.primary_movement} />
                  <ProfileField label="Speed" value={cam.movement_speed} />
                  <ProfileField label="Intensity" value={cam.movement_intensity} />
                  <ProfileField label="Stabilization" value={cam.stabilization} />
                  {cam.notes && <p className="text-[11px] text-muted-foreground mt-1">{cam.notes}</p>}
                </Section>

                {/* Color Grading */}
                <Section icon={Palette} title="Color Grading">
                  <ProfileField label="Tone" value={color.overall_tone} />
                  <ProfileField label="Contrast" value={color.contrast} />
                  <ProfileField label="Saturation" value={color.saturation} />
                  <ProfileField label="Palette Mood" value={color.color_palette_mood} />
                  {color.dominant_colors && (color.dominant_colors as string[]).length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {(color.dominant_colors as string[]).map((c: string, i: number) => (
                        <div
                          key={i}
                          className="h-4 w-4 rounded border border-border"
                          style={{ backgroundColor: c }}
                          title={c}
                        />
                      ))}
                    </div>
                  )}
                  {color.notes && <p className="text-[11px] text-muted-foreground mt-1">{color.notes}</p>}
                </Section>

                {/* Pacing */}
                <Section icon={Scissors} title="Pacing & Rhythm">
                  <ProfileField label="Cut Frequency" value={pacing.cut_frequency} />
                  <ProfileField label="Rhythm" value={pacing.rhythm} />
                  <ProfileField label="Transitions" value={pacing.transition_style} />
                  {pacing.average_shot_duration_seconds && (
                    <ProfileField label="Avg Shot" value={`${pacing.average_shot_duration_seconds}s`} />
                  )}
                  {pacing.notes && <p className="text-[11px] text-muted-foreground mt-1">{pacing.notes}</p>}
                </Section>

                {/* Lighting */}
                <Section icon={Lightbulb} title="Lighting">
                  <ProfileField label="Source" value={light.primary_source} />
                  <ProfileField label="Quality" value={light.quality} />
                  <ProfileField label="Direction" value={light.direction} />
                  <ProfileField label="Time of Day" value={light.time_of_day} />
                  <ProfileField label="Mood" value={light.mood_lighting} />
                  {light.notes && <p className="text-[11px] text-muted-foreground mt-1">{light.notes}</p>}
                </Section>

                {/* Mood */}
                <Section icon={Sparkles} title="Mood & Feel">
                  <ProfileField label="Overall" value={mood.overall_mood} />
                  <ProfileField label="Energy" value={mood.energy_level} />
                  <ProfileField label="Audience" value={mood.target_audience} />
                  {mood.brand_feeling && (
                    <p className="text-xs mt-1 italic">&ldquo;{mood.brand_feeling}&rdquo;</p>
                  )}
                </Section>
              </div>

              {/* Production Quality */}
              {p.production_quality && (
                <>
                  <Separator className="my-4" />
                  <div className="flex items-center gap-2">
                    <Film className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Production Quality
                    </span>
                    <Badge variant="outline" className="capitalize text-xs">
                      {p.production_quality as string}
                    </Badge>
                  </div>
                </>
              )}

              {/* Prompt Hints */}
              {hints.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <Section icon={Sparkles} title="Recommended Prompt Hints">
                    <div className="flex flex-wrap gap-1.5">
                      {hints.map((hint, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {hint}
                        </Badge>
                      ))}
                    </div>
                  </Section>
                </>
              )}
            </>
          )}

          {/* Style References */}
          {reference.style_references.length > 0 && (
            <>
              <Separator className="my-4" />
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                Saved Style Profiles
              </h3>
              <div className="space-y-2">
                {reference.style_references.map((style) => (
                  <div key={style.id} className="flex items-center gap-2 rounded-md border border-border p-2">
                    <span className="text-sm flex-1 truncate">{style.name}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => toggleFavorite(style.id, style.is_favorite)}
                    >
                      <Star className={`h-3.5 w-3.5 ${style.is_favorite ? 'fill-foreground text-foreground' : 'text-muted-foreground'}`} />
                    </Button>
                  </div>
                ))}
              </div>
            </>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
